/**
 * The seven presets the app offers on first run.
 *
 * The facts here mirror src/lib/setup/scenarios.ts in the client — which leg is
 * translated, whether the other side is in a call or beside you, which sides
 * are spoken, what ends up on screen. They are data rather than prose because
 * two pages render them (the home page shows the seven; /scenarios shows what
 * each one sets and which providers can serve it) and because a claim about
 * what the app does should have exactly one source.
 *
 * Copy lives in the locale files under `scenario.<id>.*`; only the shape is
 * here. Each preset's `title` and `body` are the wizard's own title and
 * description (`setup.scenarios.<id>.*` in the client's catalogs), copied
 * verbatim, so the site names a preset the way the app does.
 */

export type ScenarioId =
  | 'understand-others'
  | 'be-heard'
  | 'subtitle-myself'
  | 'two-way-voice'
  | 'two-way-text'
  | 'face-to-face-voice'
  | 'face-to-face-text';

export interface Scenario {
  id: ScenarioId;
  /** Whose audio is translated. Mirrors the client's `mode`. */
  audio: 'participant' | 'speaker' | 'both';
  /** The other side beside you, sharing your microphone: face-to-face, the
   *  client's `otherSide: 'beside'`. Absent: they are in a call. */
  beside?: boolean;
  /** Whether your side is spoken. Mirrors `!textOnly` for a speaker leg. */
  spoken: boolean;
  /**
   * Whether the other side's translation is read aloud to you — the app's
   * Translation I hear — as the preset leaves it: `on` and `off` where the
   * preset sets it (the client's `participantSpeech`), `yours` where it leaves
   * the switch alone, which in a meeting means off until you turn it on.
   */
  heard: 'on' | 'off' | 'yours';
  /**
   * Which providers can serve it, derived from providerFitForScenario():
   * a text-only provider cannot speak; a speech-only one cannot stay silent.
   * `understand-others` works with every provider because it starts as text,
   * which is the one row of the matrix worth calling out. A `beside` preset
   * also needs a provider that offers face-to-face.
   */
  needs: 'any' | 'a-voice' | 'silence';
}

export const SCENARIOS: readonly Scenario[] = [
  { id: 'understand-others', audio: 'participant', spoken: false, heard: 'yours', needs: 'any' },
  { id: 'be-heard', audio: 'speaker', spoken: true, heard: 'off', needs: 'a-voice' },
  { id: 'subtitle-myself', audio: 'speaker', spoken: false, heard: 'off', needs: 'silence' },
  { id: 'two-way-voice', audio: 'both', spoken: true, heard: 'on', needs: 'a-voice' },
  { id: 'two-way-text', audio: 'both', spoken: false, heard: 'off', needs: 'silence' },
  { id: 'face-to-face-voice', audio: 'both', beside: true, spoken: true, heard: 'on', needs: 'a-voice' },
  { id: 'face-to-face-text', audio: 'both', beside: true, spoken: false, heard: 'off', needs: 'silence' },
];

/**
 * The shapes a provider comes in, as far as a scenario is concerned — the
 * columns of /scenarios' matrix, each labelled `provider.<kind>`: the three
 * speech capabilities, and the one that can also do face-to-face (Soniox, with
 * your own key or through KizunaAI — the client's `faceToFace` capability).
 */
export const PROVIDER_KINDS = ['textOnly', 'alwaysSpeaks', 'either', 'faceToFace'] as const;

export type ProviderKind = (typeof PROVIDER_KINDS)[number];

/**
 * A text-only provider cannot speak; one that always speaks cannot stay quiet;
 * only a face-to-face provider can serve a `beside` preset. The face-to-face
 * column can do either, so it serves everything else too.
 */
export function canServe(s: Scenario, kind: ProviderKind): boolean {
  if (kind === 'faceToFace') return true;
  if (s.beside) return false;
  if (kind === 'either') return true;
  if (kind === 'textOnly') return s.needs !== 'a-voice';
  return s.needs !== 'silence';
}

/** Locale key for a scenario's short name. */
export function titleKey(id: ScenarioId): string {
  return `scenario.${id}.title`;
}

/** Locale key for the one line under the name. */
export function bodyKey(id: ScenarioId): string {
  return `scenario.${id}.body`;
}
